import { useMemo, useState, type DragEvent } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { cardsApi, launchesApi, templatesApi, typesApi } from "../api/endpoints";
import { useSettings } from "../hooks/useSettings";
import { recomputeDays } from "../lib/recomputeDays";
import { IcoBoard } from "../components/icons";
import { BoardColumn } from "./board/Column";
import { AddDayColumn } from "./board/AddDayColumn";
import { DeleteDayDialog } from "./board/DeleteDayDialog";
import { CardModal } from "./board/CardModal";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { useCanWrite } from "../hooks/useCanWrite";
import { usePermission } from "../hooks/usePermission";
import { useLaunchEvents } from "../hooks/useLaunchEvents";
import { useBoardCardRefresh } from "../hooks/useBoardCardRefresh";
import { useLaunchPresence } from "../hooks/useCardPresence";
import { usePanScroll } from "../hooks/usePanScroll";
import type { Card as CardDto, Launch, Stage } from "../types/domain";
import { useSession } from "../hooks/useSession";
import {
  clearStoredCardDraft,
  editCardDraftKey,
} from "./board/cardDraftStorage";

type ModalState =
  | { kind: "closed" }
  | { kind: "edit"; card: CardDto };

type PendingDelete = { dayKey: string; cardCount: number } | null;

export function BoardPage() {
  // canWrite — «полный CRUD на доске» (editor/admin). Используется для
  // массовых действий (добавление дня, удаление дня — это launches.edit).
  // Создание карточки разделено отдельно: любая роль с cards.create может.
  const canWrite = useCanWrite();
  const canCreateCard = usePermission("cards", "create");
  const session = useSession();
  const { settings } = useSettings();
  const activeLaunchId = settings?.activeLaunchId ?? null;
  const headerStyle = settings?.headerStyle ?? "full";
  const [modal, setModal] = useState<ModalState>({ kind: "closed" });
  const [createCardError, setCreateCardError] = useState<string | null>(null);
  const [cardToDelete, setCardToDelete] = useState<CardDto | null>(null);
  const [deleteCardError, setDeleteCardError] = useState<string | null>(null);
  const [draggingCardId, setDraggingCardId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PendingDelete>(null);
  const queryClient = useQueryClient();

  useLaunchEvents(activeLaunchId);
  useBoardCardRefresh(activeLaunchId);
  useLaunchPresence(activeLaunchId);
  const { onMouseDown: onBoardMouseDown, panning } = usePanScroll();

  const launchQuery = useQuery({
    queryKey: ["launch", activeLaunchId],
    queryFn: async () => (await launchesApi.get(activeLaunchId!)).launch,
    enabled: !!activeLaunchId,
  });
  const typesQuery = useQuery({
    queryKey: ["types"],
    queryFn: async () => (await typesApi.list()).types,
  });
  const cardsQuery = useQuery({
    queryKey: ["cards", activeLaunchId],
    queryFn: async () => (await cardsApi.list(activeLaunchId!)).cards,
    enabled: !!activeLaunchId,
  });

  const labelledDays = useMemo(() => {
    if (!launchQuery.data) return [];
    return recomputeDays(launchQuery.data.days, launchQuery.data.d1StartDate);
  }, [launchQuery.data]);

  const cardsByDay = useMemo(() => {
    const map = new Map<string, CardDto[]>();
    for (const c of cardsQuery.data ?? []) {
      const arr = map.get(c.dayKey) ?? [];
      arr.push(c);
      map.set(c.dayKey, arr);
    }
    for (const arr of map.values()) arr.sort((a, b) => a.position - b.position);
    return map;
  }, [cardsQuery.data]);

  const cardsKey = ["cards", activeLaunchId] as const;

  const moveCardMutation = useMutation({
    mutationFn: (vars: { id: string; dayKey: string; position: number }) =>
      cardsApi.move(vars.id, { dayKey: vars.dayKey, position: vars.position }),
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: cardsKey });
      const prev = queryClient.getQueryData<CardDto[]>(cardsKey);
      if (!prev) return { prev };
      const moved = prev.find((c) => c.id === vars.id);
      if (!moved) return { prev };

      const sameDay = moved.dayKey === vars.dayKey;
      // Compact source column (without the moved card).
      const sourceRest = prev
        .filter((c) => c.dayKey === moved.dayKey && c.id !== vars.id)
        .sort((a, b) => a.position - b.position)
        .map((c, i) => ({ ...c, position: i }));
      // Destination column (without the moved card if same-day).
      const destRest = sameDay
        ? sourceRest
        : prev
            .filter((c) => c.dayKey === vars.dayKey)
            .sort((a, b) => a.position - b.position)
            .map((c, i) => ({ ...c, position: i }));
      const clampedIdx = Math.max(0, Math.min(vars.position, destRest.length));
      const withMoved = [
        ...destRest.slice(0, clampedIdx),
        { ...moved, dayKey: vars.dayKey, position: clampedIdx },
        ...destRest.slice(clampedIdx).map((c) => ({ ...c, position: c.position + 1 })),
      ];

      const next = prev.map((c) => {
        if (c.id === vars.id) {
          return withMoved.find((x) => x.id === c.id)!;
        }
        const inDest = withMoved.find((x) => x.id === c.id);
        if (inDest) return inDest;
        const inSource = sameDay ? null : sourceRest.find((x) => x.id === c.id);
        if (inSource) return inSource;
        return c;
      });
      queryClient.setQueryData<CardDto[]>(cardsKey, next);
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(cardsKey, ctx.prev);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: cardsKey });
    },
  });

  const applyTemplateMutation = useMutation({
    mutationFn: (vars: { templateId: string; launchId: string; dayKey: string }) =>
      templatesApi.apply(vars.templateId, { launchId: vars.launchId, dayKey: vars.dayKey }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: cardsKey });
      queryClient.invalidateQueries({ queryKey: ["templates"] });
    },
  });

  const createCardMutation = useMutation({
    mutationFn: (vars: { dayKey: string; typeId: string; title: string }) =>
      cardsApi.create({
        launchId: activeLaunchId!,
        dayKey: vars.dayKey,
        typeId: vars.typeId,
        title: vars.title,
        preview: "",
        status: "draft",
        time: null,
        landingId: null,
        fromTemplateId: null,
      }),
    onSuccess: ({ card }) => {
      queryClient.setQueryData<CardDto[]>(cardsKey, (current) => {
        if (current?.some((item) => item.id === card.id)) return current;
        return [...(current ?? []), card];
      });
      setModal({ kind: "edit", card });
    },
    onError: (error: Error) => setCreateCardError(error.message),
    onSettled: () => queryClient.invalidateQueries({ queryKey: cardsKey }),
  });

  const deleteCardMutation = useMutation({
    mutationFn: (card: CardDto) => cardsApi.remove(card.id),
    onSuccess: (_result, card) => {
      queryClient.setQueryData<CardDto[]>(cardsKey, (current) =>
        current?.filter((item) => item.id !== card.id),
      );
      clearStoredCardDraft(editCardDraftKey(session.data?.id ?? "unknown", card.id));
      setCardToDelete(null);
    },
    onError: (error: Error) => setDeleteCardError(error.message),
    onSettled: () => queryClient.invalidateQueries({ queryKey: cardsKey }),
  });

  const handleDrop = (
    dayKey: string,
    targetIndex: number,
    event: DragEvent<HTMLDivElement>,
  ) => {
    // Template drop has priority over card move.
    let templateId = "";
    try {
      templateId = event.dataTransfer.getData("application/x-launchlab-template");
    } catch {
      // ignore
    }
    if (templateId && activeLaunchId) {
      applyTemplateMutation.mutate({ templateId, launchId: activeLaunchId, dayKey });
      setDraggingCardId(null);
      return;
    }
    if (!draggingCardId) return;
    const id = draggingCardId;
    setDraggingCardId(null);
    moveCardMutation.mutate({ id, dayKey, position: targetIndex });
  };

  // Write the server's authoritative days list straight into the launch cache so
  // the very next render sees fresh sortOrders. Relying on invalidate alone opens
  // a race: a second click fires before refetch completes and computes `position`
  // off stale indices, which causes inserts to land far from the selected column.
  const applyFreshDays = (days: Launch["days"]) => {
    if (!activeLaunchId) return;
    queryClient.setQueryData<Launch | undefined>(["launch", activeLaunchId], (old) =>
      old ? { ...old, days } : old,
    );
  };

  const addDayMutation = useMutation({
    mutationFn: (vars: { launchId: string; stage: Stage; position: number }) =>
      launchesApi.addDay(vars.launchId, { stage: vars.stage, position: vars.position }),
    onSuccess: ({ days }) => applyFreshDays(days),
    onSettled: () => {
      if (activeLaunchId) {
        queryClient.invalidateQueries({ queryKey: ["launch", activeLaunchId] });
      }
    },
  });

  const patchDayMutation = useMutation({
    mutationFn: (vars: {
      launchId: string;
      dayKey: string;
      patch: { stage?: Stage; customLabel?: string | null };
    }) => launchesApi.patchDay(vars.launchId, vars.dayKey, vars.patch),
    onSuccess: ({ days }) => applyFreshDays(days),
    onSettled: () => {
      if (activeLaunchId) {
        queryClient.invalidateQueries({ queryKey: ["launch", activeLaunchId] });
      }
    },
  });

  const deleteDayMutation = useMutation({
    mutationFn: (vars: { launchId: string; dayKey: string; migrateCardsTo?: string }) =>
      launchesApi.deleteDay(vars.launchId, vars.dayKey, vars.migrateCardsTo),
    onSuccess: ({ days }) => applyFreshDays(days),
    onSettled: () => {
      if (activeLaunchId) {
        queryClient.invalidateQueries({ queryKey: ["launch", activeLaunchId] });
        queryClient.invalidateQueries({ queryKey: cardsKey });
      }
    },
  });

  const dayMutationBusy =
    addDayMutation.isPending || patchDayMutation.isPending || deleteDayMutation.isPending;

  const handleAddDayBefore = (dayKey: string) => {
    if (!activeLaunchId) return;
    const idx = labelledDays.findIndex((d) => d.id === dayKey);
    if (idx === -1) return;
    addDayMutation.mutate({
      launchId: activeLaunchId,
      stage: labelledDays[idx]!.stage,
      position: idx,
    });
  };

  const handleAddDayAfter = (dayKey: string) => {
    if (!activeLaunchId) return;
    const idx = labelledDays.findIndex((d) => d.id === dayKey);
    if (idx === -1) return;
    addDayMutation.mutate({
      launchId: activeLaunchId,
      stage: labelledDays[idx]!.stage,
      position: idx + 1,
    });
  };

  const handleAddDayEnd = (stage: Stage) => {
    if (!activeLaunchId) return;
    addDayMutation.mutate({
      launchId: activeLaunchId,
      stage,
      position: labelledDays.length,
    });
  };

  const handleChangeStage = (dayKey: string, stage: Stage) => {
    if (!activeLaunchId) return;
    patchDayMutation.mutate({ launchId: activeLaunchId, dayKey, patch: { stage } });
  };

  const handleRenameDay = (dayKey: string, customLabel: string | null) => {
    if (!activeLaunchId) return;
    patchDayMutation.mutate({
      launchId: activeLaunchId,
      dayKey,
      patch: { customLabel },
    });
  };

  const handleDeleteDayRequest = (dayKey: string) => {
    if (!activeLaunchId) return;
    if (labelledDays.length <= 1) return;
    const count = (cardsQuery.data ?? []).filter((c) => c.dayKey === dayKey).length;
    if (count === 0) {
      deleteDayMutation.mutate({ launchId: activeLaunchId, dayKey });
      return;
    }
    setPendingDelete({ dayKey, cardCount: count });
  };

  const handleConfirmDelete = (migrateCardsTo: string | null) => {
    if (!activeLaunchId || !pendingDelete) return;
    deleteDayMutation.mutate({
      launchId: activeLaunchId,
      dayKey: pendingDelete.dayKey,
      migrateCardsTo: migrateCardsTo ?? undefined,
    });
    setPendingDelete(null);
  };

  if (!activeLaunchId) {
    return (
      <div className="launches-empty">
        <div className="launches-empty-icon">
          <IcoBoard size={24} stroke="var(--text-muted)" />
        </div>
        <h3>Не выбран запуск</h3>
        <p>
          Откройте запуск со страницы «Запуски» — и вы попадёте прямо на его доску.
        </p>
        <Link to="/launches" className="btn-primary" style={{ marginTop: 14 }}>
          К запускам →
        </Link>
      </div>
    );
  }

  if (launchQuery.isLoading || typesQuery.isLoading || cardsQuery.isLoading) {
    return (
      <div className="launches-empty">
        <p>Загружаем доску…</p>
      </div>
    );
  }

  if (launchQuery.isError || !launchQuery.data) {
    return (
      <div className="launches-empty">
        <h3>Запуск не найден</h3>
        <p>
          Возможно, он удалён или вы не имеете к нему доступа.{" "}
          <Link to="/launches">Вернуться к списку</Link>.
        </p>
      </div>
    );
  }

  const launch = launchQuery.data;
  const types = typesQuery.data ?? [];
  const cards = cardsQuery.data ?? [];
  const salesDay = labelledDays.find((d) => d.stage === "sales");

  const handleAddCard = (dayKey: string) => {
    if (createCardMutation.isPending) return;
    const defaultType = types.find((type) => type.id === "t_email") ?? types[0];
    if (!defaultType) return;
    setCreateCardError(null);
    createCardMutation.mutate({
      dayKey,
      typeId: defaultType.id,
      title: defaultType.name.trim() || "Карточка",
    });
  };

  return (
    <>
      <div className="board-head">
        <div className="board-title-wrap">
          <h1>Доска запуска</h1>
          <div className="board-subtitle">
            {launch.name} · <b>{cards.length}</b> карточек · {labelledDays.length} дней
            {salesDay && (
              <>
                {" · старт "}
                <b>
                  {salesDay.weekday.slice(0, 2).toLowerCase()} {salesDay.date}
                </b>
              </>
            )}
          </div>
        </div>
      </div>
      <div
        className={`board-scroll${panning ? " is-panning" : ""}`}
        onMouseDown={onBoardMouseDown}
      >
        <div className="board">
          {labelledDays.map((d) => (
            <BoardColumn
              key={d.id}
              day={d}
              cards={cardsByDay.get(d.id) ?? []}
              types={types}
              headerStyle={headerStyle}
              draggingCardId={draggingCardId}
              canDelete={labelledDays.length > 1}
              canWrite={canWrite}
              canCreateCard={canCreateCard}
              onOpenCard={(card) => setModal({ kind: "edit", card })}
              onDeleteCard={(card) => {
                setDeleteCardError(null);
                setCardToDelete(card);
              }}
              onAddCard={handleAddCard}
              cardCreationBusy={createCardMutation.isPending}
              onDragStartCard={setDraggingCardId}
              onDragEndCard={() => setDraggingCardId(null)}
              onDrop={handleDrop}
              onAddDayBefore={() => handleAddDayBefore(d.id)}
              onAddDayAfter={() => handleAddDayAfter(d.id)}
              onChangeStage={(stage) => handleChangeStage(d.id, stage)}
              onRenameDay={(label) => handleRenameDay(d.id, label)}
              onDeleteDay={() => handleDeleteDayRequest(d.id)}
              dayMutationBusy={dayMutationBusy}
            />
          ))}
          {canWrite && <AddDayColumn onAdd={handleAddDayEnd} disabled={dayMutationBusy} />}
        </div>
      </div>
      {modal.kind === "edit" && (
        <CardModal
          mode="edit"
          card={modal.card}
          types={types}
          days={labelledDays}
          onClose={() => setModal({ kind: "closed" })}
        />
      )}
      {createCardError && (
        <div className="toast" role="alert">
          <div>
            <b>Не удалось создать карточку</b>
            <span>{createCardError}</span>
          </div>
        </div>
      )}
      {cardToDelete && (
        <ConfirmDialog
          title="Удалить карточку?"
          message={`Карточка «${cardToDelete.title}» будет удалена с доски.`}
          confirmLabel="Удалить"
          busy={deleteCardMutation.isPending}
          onConfirm={() => deleteCardMutation.mutate(cardToDelete)}
          onClose={() => {
            if (!deleteCardMutation.isPending) setCardToDelete(null);
          }}
        />
      )}
      {deleteCardError && (
        <div className="toast" role="alert">
          <div>
            <b>Не удалось удалить карточку</b>
            <span>{deleteCardError}</span>
          </div>
        </div>
      )}
      {pendingDelete &&
        (() => {
          const day = labelledDays.find((d) => d.id === pendingDelete.dayKey);
          if (!day) return null;
          const others = labelledDays.filter((d) => d.id !== pendingDelete.dayKey);
          return (
            <DeleteDayDialog
              day={day}
              cardCount={pendingDelete.cardCount}
              otherDays={others}
              onCancel={() => setPendingDelete(null)}
              onConfirm={handleConfirmDelete}
            />
          );
        })()}
    </>
  );
}
